import type { CanonicalExam, Difficulty } from '../model/canonical.ts';
import type { QuestionType } from '../schema/question-types.ts';
import type { TaxonomyId } from '../model/brands.ts';

/**
 * Everything a session can be configured to do, in one flat object.
 *
 * The design review described three modes — exam, practice and a third that "lets you configure
 * each of these individually". That third one is not a sibling of the other two, it is the whole
 * space; the first two are points in it. So there is one policy type, two named presets that fill
 * it in, and a `custom` mode that simply exposes it. The engine reads only the policy and never
 * branches on a mode name, which is what stops three code paths growing three versions of the same
 * bug.
 */
export interface SessionPolicy {
  readonly timeLimitMs: number | null;
  readonly perQuestionTimeLimitMs: number | null;
  readonly immediateFeedback: boolean;
  readonly showExplanationAfterAnswer: boolean;
  readonly showOptionFeedback: boolean;
  readonly allowBackNavigation: boolean;
  readonly allowChangeAnswer: boolean;
  readonly allowMarkForReview: boolean;
  readonly revealSelectionCount: boolean;
  readonly shuffleQuestions: boolean;
  readonly shuffleOptions: boolean;
  readonly confirmSubmitWhenIncomplete: boolean;
  readonly autoSubmitOnTimeUp: boolean;
  readonly collectConfidence: boolean;
  /** Stop the clock when the tab is hidden. Practice yes; a timed exam, no. */
  readonly pauseWhenHidden: boolean;
}

export type ModePreset = 'exam' | 'practice' | 'custom';

/**
 * A stretch of the exam file, by the numbers a person actually sees.
 *
 * 1-based and inclusive, counted in the order the file lists its questions — not in the order the
 * session draws them. "Questions 1 to 50" has to mean the same thing before and after shuffling,
 * or the control is a lie; so this is the one place where file order is load-bearing, and it is
 * also the number shown beside each question during the session.
 */
export interface QuestionRange {
  readonly from: number;
  readonly to: number;
}

export interface SelectionCriteria {
  readonly count: 'all' | number;
  /** Null means the whole file. */
  readonly range: QuestionRange | null;
  readonly categoryIds: readonly TaxonomyId[];
  readonly topicIds: readonly TaxonomyId[];
  readonly tags: readonly string[];
  readonly difficulties: readonly Difficulty[];
  readonly types: readonly QuestionType[];
  /**
   * Draw only questions answered incorrectly this many times or more, across previous sessions.
   *
   * Null means "no restriction". Unlike every other criterion this one is not a property of the
   * exam: it is answered from the local attempt history, which the caller passes in. Sessions
   * record the number rather than the resulting set, and the blueprint records the questions it
   * actually drew — so an old result stays reproducible even though the history has moved on.
   */
  readonly incorrectAtLeast: number | null;
}

export const ALL_QUESTIONS: SelectionCriteria = {
  count: 'all',
  range: null,
  categoryIds: [],
  topicIds: [],
  tags: [],
  difficulties: [],
  types: [],
  incorrectAtLeast: null,
};

/**
 * Puts a range the right way round and inside the file.
 *
 * A range typed backwards, or past the end, is a slip rather than a request for an empty exam —
 * and an empty exam is exactly what an unrepaired one produces, several screens later, with no
 * explanation.
 */
export function sanitizeRange(range: QuestionRange | null, questionCount: number): QuestionRange | null {
  if (range === null || questionCount <= 0) {
    return null;
  }

  const clamp = (value: number): number =>
    Math.min(Math.max(Math.floor(value), 1), questionCount);

  const from = clamp(range.from);
  const to = clamp(range.to);
  const ordered = from <= to ? { from, to } : { from: to, to: from };

  return ordered.from === 1 && ordered.to === questionCount ? null : ordered;
}

const MINUTE = 60_000;

/**
 * Exam mode: the conditions of the real thing.
 *
 * Nothing is revealed until submission, the clock runs whether or not the tab is in front, and the
 * questions and options are shuffled so a second attempt is not a memory test of positions.
 */
export function examPreset(exam: CanonicalExam): SessionPolicy {
  const minutes = exam.meta.defaults.timeLimitMinutes;
  return {
    timeLimitMs: minutes === undefined ? null : minutes * MINUTE,
    perQuestionTimeLimitMs: null,
    immediateFeedback: false,
    showExplanationAfterAnswer: false,
    showOptionFeedback: false,
    allowBackNavigation: true,
    allowChangeAnswer: true,
    allowMarkForReview: true,
    revealSelectionCount: true,
    shuffleQuestions: true,
    shuffleOptions: true,
    confirmSubmitWhenIncomplete: true,
    autoSubmitOnTimeUp: true,
    collectConfidence: false,
    pauseWhenHidden: false,
  };
}

/**
 * Practice mode: learning rather than measuring.
 *
 * No clock, feedback and explanation as soon as an answer is committed, and the answer locked once
 * it is — changing it after seeing the explanation would only teach the explanation.
 */
export function practicePreset(): SessionPolicy {
  return {
    timeLimitMs: null,
    perQuestionTimeLimitMs: null,
    immediateFeedback: true,
    showExplanationAfterAnswer: true,
    showOptionFeedback: true,
    allowBackNavigation: true,
    allowChangeAnswer: false,
    allowMarkForReview: true,
    revealSelectionCount: true,
    shuffleQuestions: false,
    shuffleOptions: false,
    confirmSubmitWhenIncomplete: true,
    autoSubmitOnTimeUp: false,
    collectConfidence: false,
    pauseWhenHidden: true,
  };
}

export function presetFor(mode: ModePreset, exam: CanonicalExam): SessionPolicy {
  switch (mode) {
    case 'exam':
      return examPreset(exam);
    case 'practice':
      return practicePreset();
    case 'custom':
      // Custom starts from exam mode and is then edited; starting from the stricter of the two
      // means a forgotten switch errs towards measuring rather than towards revealing answers.
      return examPreset(exam);
  }
}

/**
 * Repairs a policy that cannot be honoured, rather than letting the engine meet it later.
 *
 * A time limit of zero, or a negative per-question limit, would surface as an exam that submits
 * itself the instant it starts.
 */
export function sanitizePolicy(policy: SessionPolicy): SessionPolicy {
  const positiveOrNull = (value: number | null): number | null =>
    value === null || value <= 0 ? null : Math.floor(value);

  const timeLimitMs = positiveOrNull(policy.timeLimitMs);

  return {
    ...policy,
    timeLimitMs,
    perQuestionTimeLimitMs: positiveOrNull(policy.perQuestionTimeLimitMs),
    // Feedback after each answer is what practice means; without it the other two switches describe
    // a state the UI has no way to reach.
    showExplanationAfterAnswer: policy.immediateFeedback && policy.showExplanationAfterAnswer,
    showOptionFeedback: policy.immediateFeedback && policy.showOptionFeedback,
    autoSubmitOnTimeUp: timeLimitMs === null ? false : policy.autoSubmitOnTimeUp,
  };
}
